import { Injectable, Optional } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';

import { firstValueFrom } from 'rxjs';

import { FeatureFlag, FeatureFlagService } from '@nx-ahs/feature-flags';

import { AppCoreService } from './app-core.service';
import { LanguageService } from './language.service';
import { ModalService } from './modal.service';

import versionData from '../../../../../../version.json';

@Injectable({
	providedIn: 'root'
})
export class AppVersionService {
	private readonly appVersion: string;
	private isPromptingRefresh: boolean = false;

	constructor(
		private swUpdate: SwUpdate,
		private appCoreService: AppCoreService,
		private languageService: LanguageService,
		private modalService: ModalService,
		@Optional() private featureFlagService: FeatureFlagService
	) {
		const rawVersion = (versionData as { version?: string })?.version ?? 'unknown';

		// Only treat 0.0.0 as "unknown" if the version check feature flag is enabled
		// The legacy system expects 0.0.0 when the feature is disabled
		if (rawVersion === '0.0.0') {
			this.appVersion = this.isVersionCheckEnabledAtStartup() ? 'unknown' : '0.0.0';
		} else {
			this.appVersion = rawVersion;
		}

		// Listen for updates in the background (only if SW is enabled)
		if (this.swUpdate.isEnabled) {
			this.swUpdate.versionUpdates.subscribe((evt) => {
				if (evt.type === 'VERSION_READY') {
					console.log('New app version ready');
				}
			});
		}
	}

	getVersion(): string {
		return this.appVersion;
	}

	async checkVersionMismatch(minimumVersion: string): Promise<void> {
		if (minimumVersion && !this.isVersionSatisfied(this.appVersion, minimumVersion)) {
			await this.promptVersionRefreshLegacy();
		}
	}

	/**
	 * Checks if the PortalVersionCheck feature flag is enabled.
	 * Returns true if the flag is enabled or if the feature flag service is not available.
	 */
	private isVersionCheckEnabled(): boolean {
		// If feature flag service is not available, default to enabled
		if (!this.featureFlagService || !this.featureFlagService.features) {
			return true;
		}

		return this.featureFlagService.getFeature(FeatureFlag.PortalVersionCheck);
	}

	/**
	 * Checks if the PortalVersionCheck feature flag is enabled at startup.
	 * Returns false if the flag service is not available (maintains backwards compatibility).
	 */
	private isVersionCheckEnabledAtStartup(): boolean {
		// If feature flag service is not available or features not loaded, default to disabled
		// This maintains backwards compatibility with the legacy system that expects 0.0.0
		if (!this.featureFlagService || !this.featureFlagService.features) {
			return false;
		}

		return this.featureFlagService.getFeature(FeatureFlag.PortalVersionCheck);
	}

	/**
	 * Compares two version strings in the format "YYYY.MM.DD.build" (e.g., "2025.02.06.01").
	 * Returns true if the client version is greater than or equal to the minimum version.
	 */
	private isVersionSatisfied(clientVersion: string, minimumVersion: string): boolean {
		// Unknown client version is never satisfied - require refresh
		if (!clientVersion || clientVersion === 'unknown') {
			return false;
		}

		const clientParts = this.parseVersionParts(clientVersion);
		const minimumParts = this.parseVersionParts(minimumVersion);

		if (!clientParts || !minimumParts) {
			// If parsing fails, fall back to string comparison
			return clientVersion >= minimumVersion;
		}

		// Compare each part: year, month, day, build
		for (let i = 0; i < 4; i++) {
			if (clientParts[i] > minimumParts[i]) {
				return true;
			}
			if (clientParts[i] < minimumParts[i]) {
				return false;
			}
		}

		// Versions are equal
		return true;
	}

	/**
	 * Parses a version string into an array of 4 numbers [year, month, day, build].
	 * Returns null if parsing fails.
	 */
	private parseVersionParts(version: string): number[] | null {
		if (!version || version === 'unknown') {
			return null;
		}

		const parts = version.split('.');
		if (parts.length !== 4) {
			return null;
		}

		const result: number[] = [];
		for (const part of parts) {
			const num = parseInt(part, 10);
			if (isNaN(num)) {
				return null;
			}
			result.push(num);
		}

		return result;
	}

	/**
	 * Prompts the user to refresh their browser to get the latest version.
	 * Used when the server returns a 426 (Upgrade Required) response.
	 * Respects the PortalVersionCheck feature flag.
	 * Prevents multiple modals from showing simultaneously.
	 * User must acknowledge and refresh - there is no option to decline.
	 */
	async promptVersionRefresh(): Promise<void> {
		// Check if feature flag is disabled - if so, skip version check
		if (!this.isVersionCheckEnabled()) {
			return;
		}

		await this.showVersionRefreshModal();
	}

	/**
	 * Legacy version refresh prompt used after login.
	 * Does NOT check the feature flag - always prompts if called.
	 * This preserves the existing login flow behavior.
	 */
	async promptVersionRefreshLegacy(): Promise<void> {
		await this.showVersionRefreshModal();
	}

	/**
	 * Shows the version refresh modal and forces reload.
	 * Prevents multiple modals from showing simultaneously.
	 * User must acknowledge and refresh - there is no option to decline.
	 */
	private async showVersionRefreshModal(): Promise<void> {
		// Prevent multiple modals from showing if multiple requests fail
		if (this.isPromptingRefresh) {
			return;
		}

		this.isPromptingRefresh = true;

		// If client config opts out of the modal, skip straight to the update
		if (this.appCoreService.clientConfig.appVersion?.skipRefreshModal) {
			await this.forceServiceWorkerUpdateAndReload();
			return;
		}

		// Load login page translations from database (VERSION_MISMATCH keys are on login page)
		await firstValueFrom(this.languageService.getLanguagesForPage('login'));

		// Show modal with only OK button (no option to decline)
		await this.modalService.presentMessageModal('VERSION_MISMATCH_TITLE', 'VERSION_MISMATCH_MESSAGE', false);

		// User must refresh - no option to skip
		await this.forceServiceWorkerUpdateAndReload();
	}

	/**
	 * Triggers the service worker to check for updates, install the new version,
	 * activate it, and then reload the page to use the fresh assets.
	 */
	private async forceServiceWorkerUpdateAndReload(): Promise<void> {
		if (!this.swUpdate.isEnabled) {
			// Service worker not available, fall back to simple reload
			window.location.reload();
			return;
		}

		try {
			// 1. Force the service worker to check for updates right now
			const updateFound = await this.swUpdate.checkForUpdate();

			if (updateFound) {
				// 2. Wait for the new version to be fully downloaded & ready
				await this.waitForVersionReady();

				// 3. Activate the new version immediately (skip waiting)
				await this.swUpdate.activateUpdate();

				console.log('New version activated');
			} else {
				console.log('No update found - forcing reload anyway');
			}

			// 4. Reload the page - now it will use the new SW cache
			window.location.reload();
		} catch (err) {
			console.error('Service worker update failed:', err);
			// Fallback: just hard reload if SW update fails
			window.location.reload();
		}
	}

	/**
	 * Waits for VERSION_READY event (new version downloaded and ready to activate)
	 */
	private waitForVersionReady(): Promise<void> {
		return new Promise((resolve) => {
			const subscription = this.swUpdate.versionUpdates.subscribe((evt) => {
				if (evt.type === 'VERSION_READY') {
					subscription.unsubscribe();
					resolve();
				}
			});

			// Safety timeout (30 seconds) in case event never fires
			setTimeout(() => {
				subscription.unsubscribe();
				resolve(); // proceed anyway
			}, 30000);
		});
	}
}
